import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { HttpHeaders } from '@angular/common/http';
import * as buffer from 'buffer';
import { AttachmentFile } from '../models/users/dto/attachment-file';
import { Endpoints } from './endpoints';
import { SubmissionAttachment } from '../models/submissions/submission-attachment';
import { CreateAttachmentRequest } from '../models/users/requests/create-attachment-request';
import { MediaUtils } from '../utils/media-utils';

@Injectable({
  providedIn: 'root'
})
export class AttachmentApi implements LoggableAPI {
  constructor() {}

  private apiClient = inject(ApiClient);

  public serviceName = 'Attachments';

  public uploadToPresignedLinks(
    files: File[],
    attachmentRequests: CreateAttachmentRequest[],
    submissionAttachments: SubmissionAttachment[] | null | undefined
  ): Observable<boolean> {
    const uploads = files.map((file, index) => {
      const fileName = attachmentRequests[index].fileName;
      const attachment = submissionAttachments?.find(a => a.attachment.fileName === fileName);
      const presignedUrl = attachment?.attachment.links?.[0]?.presignedUrl;
      if (!attachment || !presignedUrl) return of(false);
      return from(MediaUtils.fileToUserFile(file)).pipe(
        switchMap(userFile =>
          this.uploadAttachmentFile(new AttachmentFile(file, attachment.id, userFile.fileUrl, presignedUrl))
        ),
        map(() => true),
        catchError(() => of(false))
      );
    });
    if (!uploads.length) return of(true);
    return forkJoin(uploads).pipe(map(results => results.every(uploaded => uploaded)));
  }

  public uploadAttachmentFile(file: AttachmentFile): Observable<any> {
    const fileType = file.getFileType();
    const headers = new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': fileType,
      'x-amz-meta-attachmentid': file.attachmentId
    });
    const replacementString = 'data:' + fileType + ';base64,';
    const newFileName = file.fileUrl.replace(replacementString, '');
    const buff = buffer.Buffer.from(newFileName, 'base64');

    const blob = new Blob([new Uint8Array(buff)]);
    return this.apiClient.putAssetToS3(file.presignedUrl, blob, headers).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteMemberSubmissionAttachment(attachmentId: string, submissionId: string): Observable<any> {
    const url = Endpoints.ipDeleteMemberSubmissionAttachment(attachmentId, submissionId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteEmployerSubmissionAttachment(attachmentId: string, submissionId: string): Observable<any> {
    const url = Endpoints.ipDeleteEmployerSubmissionAttachment(attachmentId, submissionId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionAttachments(submissionId: string): Observable<SubmissionAttachment[]> {
    const url = Endpoints.ipGetMemberSubmissionAttachments(submissionId);
    return this.apiClient.getArr<SubmissionAttachment>(SubmissionAttachment, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionAttachments(submissionId: string): Observable<SubmissionAttachment[]> {
    const url = Endpoints.ipGetEmployerSubmissionAttachments(submissionId);
    return this.apiClient.getArr<SubmissionAttachment>(SubmissionAttachment, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
